/**
 * API client for the Domino Balance game.
 *
 * Each function wraps a fetch() call to the backend routes
 * at /api/games/domino-balance/*.
 */

import { logger } from '../../lib/logger.js';
import type {
  StartGameRequest,
  StartGameResponse,
  MoveRequest,
  MoveResponse,
  RepositionRequest,
  UndoResponse,
  CheckResponse,
  RevealResponse,
  ActiveGameResponse,
  StatsResponse,
  HintResponse,
} from './types.js';

const BASE = '/api/games/domino-balance';

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const resp = await fetch(url, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...options.headers as Record<string, string> },
    ...options,
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const msg = (body as Record<string, string>).error ?? `Request failed (${resp.status})`;
    throw new Error(msg);
  }
  return resp.json() as Promise<T>;
}

export async function startGame(req: StartGameRequest): Promise<StartGameResponse> {
  logger.info('[DominoBalance] Starting game', req);
  return request<StartGameResponse>(`${BASE}/start`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function makeMove(req: MoveRequest): Promise<MoveResponse> {
  return request<MoveResponse>(`${BASE}/move`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function repositionTile(req: RepositionRequest): Promise<MoveResponse> {
  return request<MoveResponse>(`${BASE}/reposition`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function undoMove(gameId: string): Promise<UndoResponse> {
  return request<UndoResponse>(`${BASE}/undo`, {
    method: 'POST',
    body: JSON.stringify({ game_id: gameId }),
  });
}

export async function checkSolution(gameId: string): Promise<CheckResponse> {
  return request<CheckResponse>(`${BASE}/check`, {
    method: 'POST',
    body: JSON.stringify({ game_id: gameId }),
  });
}

export async function getActiveGame(): Promise<ActiveGameResponse> {
  return request<ActiveGameResponse>(`${BASE}/active`);
}

export async function getStats(): Promise<StatsResponse> {
  return request<StatsResponse>(`${BASE}/stats`);
}

export async function getHint(gameId: string): Promise<HintResponse> {
  return request<HintResponse>(`${BASE}/hint`, {
    method: 'POST',
    body: JSON.stringify({ game_id: gameId }),
  });
}

export async function revealSolution(gameId: string): Promise<RevealResponse> {
  return request<RevealResponse>(`${BASE}/reveal`, {
    method: 'POST',
    body: JSON.stringify({ game_id: gameId }),
  });
}

export async function abandonGame(gameId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`${BASE}/abandon`, {
    method: 'POST',
    body: JSON.stringify({ game_id: gameId }),
  });
}
